<li
    {id}
    bind:this={cardElement}
    class="topic-card {hiddenClass} {hoverSupportClass}"
    role={keyboardReorder ? "option" : undefined}
    class:topic-card--shadow={dragAndDropSelected}
    class:topic-card--drag={keyboardReorderSelected}
    aria-selected={keyboardReorder ? keyboardReorderSelected : undefined}
    aria-labelledby="{id}--title"
>
    <a {href} class="topic-card__link" aria-labelledby="{id}--title">
        <span class="screen-reader">{name}</span>
    </a>

    <div class="topic-card__header">
        {#if showSelect || showDrag}
            <div class="topic-card__controls">
                {#if showSelect}
                    <Checkbox
                        bind:checked
                        label="{i18n.select} {name}"
                        labelHidden
                        small
                        on:change={updateCheckedStatus(topicId, checked)}
                    />
                {/if}
                {#if showDrag}
                    <button
                        bind:this={dragButton}
                        id="{id}--drag"
                        class="topic-card__drag"
                        on:mousedown={mouseDownDragButton}
                        on:touchstart={mouseDownDragButton}
                        on:click={clickDragButton}
                        on:keyup={keyUpDragButton}
                        on:blur={blurDragButton}
                        aria-labelledby="{id}--title"
                        aria-pressed={keyboardReorderSelected}
                    ></button>
                    <div
                        class="topic-card__tooltip-container"
                        aria-live="polite"
                        class:topic-card__tooltip-container--visible={tooltipVisible}
                    >
                        {#if tooltipVisible}
                            <Tooltip
                                element={dragButton}
                                id="{id}-drag-tip"
                                placement="bottom"
                                role="tooltip"
                                show={tooltipVisible}
                                text={i18n.elementDragHandleTooltip}
                            />
                        {/if}
                    </div>
                {/if}
            </div>
        {/if}

        {#if !uncategorized}
            <div class="topic-card__image" style={colorStyle}></div>
        {/if}

        <div class="topic-card__info">
            <div class="topic-card__title" id="{id}--title">
                <span>{name}</span>
                <div class="topic-card__hidden-indicator">
                    <span class="screen-reader">{i18n.hidden}</span>
                </div>
            </div>

            <div class="topic-card__details">{infoText}</div>
        </div>
    </div>
</li>

<script>
    import { createEventDispatcher } from "svelte";
    import { get } from "svelte/store";

    import Checkbox from "@itslearning/prometheus/assets/inputs/Checkbox/v1/Checkbox.svelte";
    import Tooltip from "@itslearning/prometheus/assets/modals/Tooltip/v1/Tooltip.svelte";

    import { formatLanguageString } from "@itslearning/atlas/strings/format.js";
    import { isMobile, isIpad } from "@itslearning/atlas/detectors/UserAgentHelper";
    import { Keys } from "@itslearning/atlas/keyboard/keys";
    import { normalizeKey } from "@itslearning/atlas/keyboard/normalize";

    import { getDateRangeString } from "@its-ui/planner-common/helpers/dateHelper";
    import { adjustToTimezone } from "@its-ui/planner-common/helpers/loadPlansHelper";
    import i18n from "@its-ui/planner-common/i18n";
    import config from "@its-ui/planner-common/config";
    import { selectedTopicIds } from "@its-ui/planner-common/logic/plans/stores";
    import { columnTypes, columnVisibility } from "@its-ui/planner-common/constants";


    export let id = Math.random().toString(36).substr(2, 9);
    export let checked = false;
    export let name;
    export let href;
    export let color;
    export let uncategorized = false;
    export let startDate;
    export let endDate;
    export let showSelect = false;
    export let showDrag = false;
    export let visible = true;
    export let planCount = 0;
    export let keyboardReorderSelected;
    export let dragAndDropSelected;
    export let keyboardReorder;
    export let topicId;

    let tooltipVisible = false;
    let cardElement;
    let dragButton;

    const dispatch = createEventDispatcher();

    $: colorStyle = color ? `background-color: ${color};` : "";

    let infoText = "";

    $: {
        if (planCount === 0) {
            infoText = i18n.noPlans;
        } else if (
            startDate &&
            config.canEdit &&
            config.columnVisibilities[columnTypes.Date] !== columnVisibility.Nobody
        ) {
            infoText =
                planCount === 1
                    ? i18n.onePlanWithDate
                    : formatLanguageString(i18n.xPlansWithDate, planCount);

            const dateString = getDateRangeString(
                adjustToTimezone(startDate),
                adjustToTimezone(endDate),
                config.locale
            );

            infoText = infoText.replace(/{\d}/, dateString);
        } else if (
            startDate &&
            !config.canEdit &&
            config.columnVisibilities[columnTypes.Date] === columnVisibility.All
        ) {
            infoText =
                planCount === 1
                    ? i18n.onePlanWithDate
                    : formatLanguageString(i18n.xPlansWithDate, planCount);

            const dateString = getDateRangeString(
                adjustToTimezone(startDate),
                adjustToTimezone(endDate),
                config.locale
            );

            infoText = infoText.replace(/{\d}/, dateString);
        } else {
            infoText =
                planCount === 1 ? i18n.onePlan : formatLanguageString(i18n.xPlans, planCount);
        }
    }

    $: hiddenClass = visible ? "" : "topic-card--hidden";
    $: hoverSupportClass = isMobile() || isIpad() ? "" : "itsl-topic-card--hover-support";

    function mouseDownDragButton(e) {
        dispatch("dragStart", e);
    }

    function clickDragButton(e) {
        dispatch("keyboardDragStart", e);
    }

    function keyUpDragButton(e) {
        if (normalizeKey(e.key) === Keys.TAB) {
            tooltipVisible = true;
        }
    }

    function blurDragButton() {
        tooltipVisible = false;
    }

    function updateCheckedStatus(id, checked) {
        const selectedTopics = get(selectedTopicIds);

        if (!checked) {
            selectedTopics.delete(id);
        } else {
            selectedTopics.add(id);
        }

        selectedTopicIds.set(selectedTopics);
    }
</script>
